import React, { useState } from 'react';
import { useApolloClient, gql } from '@apollo/client';
import PropTypes from 'prop-types';
import Dialog from '../Dialog';
import Container from '../Container';
import Text from '../Text';
import SelectButton from '../SelectButton';
import Icon from '../Icon';
import TextArea from '../TextArea';
import Uploader from '../Uploader';
import Image from '../Image';
import EmptyButton from '../EmptyButton';
import FancyInput from '../FancyInput';
import Button from '../Button';
import NotificationSlider from '../NotificationSlider';
import AlertDialog from '../AlertDialog';
import Validator from '../Validator';
import useForm from '../../hooks/useForm';
import Camera from './Camera';
import Animation from '../Animation';
import Anchor from '../Anchor';
import envelope from './envelope.json';

const createFeedbackMutation = gql`
  mutation createFeedback($input: CreateFeedbackInput!) {
    createFeedback(input: $input) {
      feedback {
        name
      }
    }
  }
`;

const uploadErrorMessages = {
  maxUploads: `The maximum number of images you're allowed to upload is 3. `,
  maxFileSize: `Oops, your file size is too big. Please try again ensuring the file is a maximum of 10MB. `,
  fileType:
    "Oops, you've selected an invalid file format. You can try again with a .jpg, .jpeg or .png file. ",
  upload: 'Oops, something went wrong.',
};

const TYPES = [
  {
    label: 'Incorrect Details',
    icon: (
      <Icon iconSize="30px" className="ls-icon icon-categorybusinessservices" />
    ),
  },
  {
    label: 'Missing Business',
    icon: (
      <Icon
        iconSize="30px"
        marginRight="7px"
        className="ls-icon icon-categoryhomeimlookingfor"
      />
    ),
  },
  {
    label: 'Technical Issue',
    icon: <Icon iconSize="30px" className="ls-icon icon-generalalert" />,
  },
  {
    label: 'Suggestion',
    icon: (
      <Icon
        iconSize="30px"
        marginBottom="-3px"
        className="ls-icon icon-categoryboatlighting"
      />
    ),
  },
];

const FeedbackForm = ({
  show,
  mode,
  onClose,
  application,
  extraTypes,
  ...props
}) => {
  const [assets, setAssets] = useState([]);
  const [uploadErrorMessage, setUploadErrorMessage] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [showAlertDialog, setShowAlertDialog] = useState(false);
  const client = useApolloClient();

  const {
    values: { type = '', name = '', email = '', message = '' },
    setValue,
    setValueDebounce,
    validation,
    onSubmit,
    valid,
    changed,
    loading,
    error,
    reset,
  } = useForm({
    onSubmit: async () => {
      await client.mutate({
        mutation: createFeedbackMutation,
        variables: {
          input: {
            type,
            name,
            email,
            message,
            application,
            assets: assets.length ? assets : null,
            url: window.location.href,
          },
        },
      });
      setShowSuccess(true);
      reset();
      setAssets([]);
      onClose();
    },
  });

  const { valueMissing, typeMismatch, dirty } = validation;

  const onTypeClick = value => {
    setValue({ name: 'type', value });
  };

  const onUploadError = errors => {
    if (errors.error) {
      setUploadErrorMessage(
        uploadErrorMessages[errors.error] || 'Oops, something went wrong.'
      );
    }
  };

  const onRemoveImageClick = (e, i) => {
    e.preventDefault();
    setAssets(assets.filter((asset, index) => i !== index));
  };

  const onUploadSuccess = files => {
    if (assets.length === 3) {
      setUploadErrorMessage(uploadErrorMessages.maxUploads);
      return;
    }
    setAssets([...files.map(({ src }) => src), ...assets]);
    setUploadErrorMessage('');
  };

  return (
    <>
      <Dialog
        zIndex="99999999"
        show={show}
        maxWidth="495px"
        border="none"
        mode={mode}
        {...props}
      >
        <form onSubmit={onSubmit}>
          <Container
            backgroundColor={theme => theme.primary.normal}
            padding="20px"
            color="white"
            fontSize="24px"
            fontWeight="600"
            borderTopLeftRadius="11px"
            borderTopRightRadius="11px"
            display="flex"
            justifyContent="space-between"
          >
            <Text>Send Feedback</Text>
            <EmptyButton
              onClick={() => {
                if (changed()) {
                  setShowAlertDialog(true);
                } else {
                  setAssets([]);
                  onClose();
                }
              }}
              display="flex"
              alignItems="center"
            >
              <Icon
                color="white"
                iconSize="50px"
                className="ls-icon icon-generalxlarge"
                lineHeight="0.5"
                height="0.4em"
                width="0.7em"
              />
            </EmptyButton>
          </Container>
          <Container padding="20px">
            <Text
              display="block"
              marginBottom="20px"
              fontSize="16px"
              color="normal"
              fontWeight="600"
              lineHeight="1.2"
            >
              What&apos;s your feedback about?
            </Text>
            <Container display="flex" flexWrap="wrap" marginBottom="15px">
              {[...TYPES, ...extraTypes].map(item => (
                <SelectButton
                  key={item.label}
                  onClick={() => onTypeClick(item.label)}
                  selected={type === item.label}
                  paddingLeft="10px"
                >
                  {item.icon}
                  {item.label}
                </SelectButton>
              ))}
            </Container>
            <Container
              marginBottom="15px"
              height="1px"
              backgroundColor="background400"
            />
            <Text
              display="block"
              marginBottom="20px"
              fontSize="16px"
              color="normal"
              fontWeight="600"
              lineHeight="1.2"
            >
              Tell us more...
            </Text>
            <TextArea
              name="message"
              fontFamily="Circular"
              onChange={setValue}
              value={message}
              placeholder="Please tell us more about what's going on or your suggestion.*"
              maxLength={1000}
              required
            />
            <Container display="flex" justifyContent="flex-end">
              <Text fontSize="12px" color="normal">
                {message.length}
                /1000
              </Text>
            </Container>
            <Container marginBottom="15px">
              <Validator>
                {valueMissing('message') && 'This field is required.'}
              </Validator>
            </Container>
            <Container
              marginBottom="15px"
              height="1px"
              backgroundColor="background400"
            />
            <Text
              display="block"
              lineHeight="1.2"
              fontSize="16px"
              color="normal"
              fontWeight="600"
              marginBottom="5px"
            >
              Have a photo to support your suggestion?
            </Text>
            <Text
              display="block"
              lineHeight="1.3"
              fontSize="14px"
              color="text300"
              marginBottom="15px"
            >
              Photos can help us confirm the information you provide.
            </Text>
            <Uploader
              onUploadSuccess={onUploadSuccess}
              onError={onUploadError}
              name="assets"
              maxUploads={3}
              multiple
            >
              <Container display="flex" flexWrap="wrap" margin="0 -5px">
                <Container
                  borderRadius={assets.length ? '5px' : '30px'}
                  width={assets.length ? '60px' : 'auto'}
                  height={assets.length ? '60px' : '40px'}
                  border={theme => `1px solid ${theme.primary.primary200}`}
                  margin="5px"
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  position="relative"
                  padding={assets.length ? 0 : '0 20px'}
                >
                  <Camera />
                  {!assets.length ? (
                    <Text marginLeft="5px" color="text700" fontSize="14px">
                      Upload Image
                    </Text>
                  ) : null}
                </Container>
                {assets.map((src, i) => (
                  <Container
                    key={src}
                    flex="0 0 60px"
                    height="60px"
                    margin="5px"
                    position="relative"
                  >
                    <Container
                      position="absolute"
                      right="-8px"
                      marginTop="-8px"
                      border="1px solid white"
                      borderRadius="100%"
                      width="22px"
                      height="22px"
                      backgroundColor="rgba(0,0,0,0.6)"
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                    >
                      <EmptyButton onClick={e => onRemoveImageClick(e, i)}>
                        <Icon
                          marginRight="-2px"
                          marginTop="5px"
                          fontSize="27px"
                          color="white"
                          className="ls-icon icon-generalxlarge"
                        />
                      </EmptyButton>
                    </Container>
                    <Container
                      width="100%"
                      height="100%"
                      border={theme => `1px solid ${theme.primary.primary200}`}
                      borderRadius="5px"
                      overflow="hidden"
                    >
                      <Image
                        height="100%"
                        width="100%"
                        objectFit="cover"
                        src={src}
                      />
                    </Container>
                  </Container>
                ))}
              </Container>
            </Uploader>
            <Validator show={!!uploadErrorMessage}>
              {uploadErrorMessage}
            </Validator>
            <Container
              marginBottom="15px"
              marginTop="15px"
              height="1px"
              backgroundColor="background400"
            />
            <Text
              display="block"
              marginBottom="20px"
              fontSize="16px"
              color="normal"
              fontWeight="600"
              lineHeight="1.2"
            >
              Please fill in your details
            </Text>
            <Container marginBottom="15px">
              <FancyInput
                name="name"
                value={name}
                onChange={setValueDebounce}
                fontSize="14px"
                paddingBottom="10px"
                placeholder="Name*"
                showClear={false}
                required
                icon={
                  <Container
                    width="35px"
                    display="flex"
                    justifyContent="center"
                  >
                    <Icon
                      iconColor={theme => theme.text.text300}
                      iconSize="30px"
                      className="ls-icon icon-categoryclubs"
                    />
                  </Container>
                }
              />
              <Container
                display="flex"
                justifyContent="flex-end"
                marginTop="3px"
              >
                <Validator fontSize="14px">
                  {valueMissing('name')
                    ? 'Let us know what we can call you.'
                    : null}
                </Validator>
              </Container>
            </Container>
            <Container marginBottom="25px">
              <FancyInput
                name="email"
                type="email"
                value={email}
                onChange={setValueDebounce}
                fontSize="14px"
                paddingBottom="10px"
                placeholder="Email*"
                required
                showClear={false}
                icon={
                  <Container
                    width="35px"
                    display="flex"
                    justifyContent="center"
                  >
                    <Icon
                      iconColor={theme => theme.text.text300}
                      iconSize="30px"
                      className="ls-icon icon-categorypostoffices"
                    />
                  </Container>
                }
              />
              <Container
                display="flex"
                justifyContent="flex-end"
                marginTop="3px"
              >
                <Validator fontSize="14px">
                  {typeMismatch('email') || valueMissing('email')
                    ? 'Oops, that isn’t a valid email.'
                    : null}
                </Validator>
              </Container>
            </Container>
            <Container
              marginBottom="5px"
              display="flex"
              justifyContent="flex-end"
            >
              <Validator>{error && 'Oops, something went wrong.'}</Validator>
            </Container>
            <Container
              display="flex"
              justifyContent="flex-end"
              marginBottom="10px"
            >
              <Button
                loading={loading}
                disabled={
                  !valid() ||
                  !changed() ||
                  !dirty('email') ||
                  !dirty('name') ||
                  !dirty('message') ||
                  !dirty('type')
                }
                padding="0 65px"
                width="auto"
                type="submit"
                primary
                rounded
              >
                Submit
              </Button>
            </Container>
          </Container>
        </form>
      </Dialog>
      <NotificationSlider
        duration={8000}
        zIndex="999999999"
        heading="Thanks for your feedback!"
        show={showSuccess}
        onClose={() => setShowSuccess(false)}
        text={
          <Text>
            If it&apos;s an urgent matter, you&apos;re welcome to contact our
            team on&nbsp;
            <Anchor color="text700" href="tel:1300 360 867">
              1300 360 867
            </Anchor>
            &nbsp;during business hours.
          </Text>
        }
        icon={<Animation data={envelope} />}
      />
      <AlertDialog
        zIndex="999999999"
        show={showAlertDialog}
        heading="Are you sure you want to quit?"
        text="If you quit now, this form will reset and you'll have to fill it in again."
        buttons={[
          {
            children: 'Quit',
            props: {
              backgroundColor: 'white',
              minWidth: 'unset',
              width: '115px',
              rounded: true,
              onClick: () => {
                onClose();
                setAssets([]);
                reset();
                setShowAlertDialog(false);
              },
            },
          },
          {
            children: "No, I'm not done yet!",
            props: {
              primary: true,
              rounded: true,
              onClick: () => setShowAlertDialog(false),
            },
          },
        ]}
      />
    </>
  );
};

FeedbackForm.propTypes = {
  show: PropTypes.bool,
  mode: PropTypes.string,
  onClose: PropTypes.func,
  application: PropTypes.string,
  extraTypes: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      icon: PropTypes.element.isRequired,
    })
  ),
};

FeedbackForm.defaultProps = {
  show: false,
  mode: undefined,
  onClose: () => {},
  application: null,
  extraTypes: [],
};

export default FeedbackForm;
